import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLeads } from '@saiens/shared/hooks';
import { formatCurrency, formatDate } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ChevronRight, Plus } from 'lucide-react';

export function CrmListPage() {
  const [search, setSearch] = useState('');
  const [myLeads, setMyLeads] = useState(true);

  const { data, isLoading, error } = useLeads({
    search,
    myLeads,
    limit: 50,
  });

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">專案管理</h1>
        <Link
          to="/crm/new"
          className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"
        >
          <Plus className="h-4 w-4" />
          新增
        </Link>
      </div>

      {/* Search & Filter */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋專案..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          <FilterChip
            label="我的專案"
            active={myLeads}
            onClick={() => setMyLeads(!myLeads)}
          />
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : data?.items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">沒有符合條件的專案</div>
        ) : (
          <div className="divide-y">
            {data?.items.map((lead) => (
              <Link
                key={lead.id}
                to={`/crm/${lead.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{lead.name}</p>
                  <p className="text-sm text-gray-500 truncate">
                    {lead.partner_name || '未指定客戶'}
                    {lead.project_address && ` • ${lead.project_address}`}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                    <span>{formatDate(lead.create_date)}</span>
                    {lead.user_name && <span>• {lead.user_name}</span>}
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-primary">
                      {formatCurrency(lead.expected_revenue)}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Total count */}
      {data && (
        <p className="mt-4 text-center text-sm text-gray-500">共 {data.total} 筆專案</p>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
